import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useWishlist } from '@/store/wishlist';

/**
 * Global wishlist sync — mount once at the app root.
 *  - When a user signs in, hydrate the wishlist from Supabase and migrate
 *    any anonymous local items.
 *  - When a user signs out, clear the local copy so it doesn't leak between
 *    accounts on a shared device.
 */
export function useWishlistSync() {
  const { user, loading } = useAuth();
  const hydrate = useWishlist((s) => s.hydrateFromDb);
  const clearLocal = useWishlist((s) => s.clearLocal);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;
    const uid = user?.id ?? null;
    if (uid === lastUserId.current) return;
    if (uid) {
      hydrate(uid);
    } else if (lastUserId.current) {
      // Signed out → drop the previous user's items.
      clearLocal();
    }
    lastUserId.current = uid;
  }, [user, loading, hydrate, clearLocal]);
}
